/**
 * AccountHealthCard — ✦ Account health (Account detail v3 AD3; spec v3 §3;
 * slice plan v2, ruled 2026-10-31). Renders the SERVER's six checks: the ring
 * "n of m" over the checks that apply, the badge, the headline from the
 * server's headline check, one row per check (a check that does not apply is
 * greyed with its reason — Q5 amended), and the coverage footer naming the
 * seats. Nothing here decides a verdict; words only (accountHealthView).
 */
import type { SalesAccountHealth } from '../../../../api/types'
import { ink, track } from '../../../../theme/tokens'
import {
  CHECK_LABELS, checkValue, coverageFooter, healthHeadline, statusStroke, statusTone, statusWords,
} from './accountHealthView'

function Ring({ passed, applicable, status }: { passed: number; applicable: number; status: string }) {
  const r = 22
  const c = 2 * Math.PI * r
  const share = applicable > 0 ? passed / applicable : 0
  return (
    <svg width={56} height={56} viewBox="0 0 56 56" role="img" aria-label={`${passed} of ${applicable} checks pass`}>
      <circle cx={28} cy={28} r={r} fill="none" stroke={track.DEFAULT} strokeWidth={5} />
      <circle cx={28} cy={28} r={r} fill="none" stroke={statusStroke(status)} strokeWidth={5} strokeLinecap="round"
        strokeDasharray={`${c * share} ${c}`} transform="rotate(-90 28 28)" />
      <text x={28} y={32} textAnchor="middle" fontSize={12} fontWeight={700} fill={ink.primary}>{passed}/{applicable}</text>
    </svg>
  )
}

const MARK: Record<string, string> = { pass: '✓', fail: '!', not_applicable: '–' }

export function AccountHealthCard({ health, accountName, ageDays, today }: {
  health: SalesAccountHealth
  accountName: string
  ageDays: number | null
  today: string
}) {
  const checks = health.checks ?? []
  const tone = statusTone(health.status)
  const footer = coverageFooter(checks.find(c => c.id === 'people_coverage'))
  return (
    <section className="k-card" aria-label="Account health">
      <div className="k-card-head">
        <div className="flex items-center gap-3 min-w-0">
          <Ring passed={health.passed} applicable={health.applicable} status={health.status} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="k-card-title">✦ Account health</div>
              <span className={tone ? 'k-badge' : 'k-badge-outline text-ink-muted'} style={tone ?? undefined} data-status={health.status}>
                <span className="k-badge-dot" />{statusWords(health.status)}
              </span>
            </div>
            <div className="text-13 text-ink-primary font-medium mt-0.5">{healthHeadline(health, accountName, today)}</div>
          </div>
        </div>
      </div>
      <ul className="px-5 py-2">
        {checks.map(c => {
          const na = c.state === 'not_applicable'
          return (
            <li key={c.id} data-check={c.id} data-state={c.state}
              className={`flex items-center justify-between gap-3 py-2 border-b border-edge-row last:border-0 text-13 ${na ? 'text-ink-muted' : ''}`}>
              <span className={na ? '' : 'text-ink-secondary'}>{CHECK_LABELS[c.id] ?? c.id}</span>
              <span className={`text-right ${na ? '' : c.state === 'pass' ? 'text-ink-primary' : 'text-ink-primary font-medium'}`}>
                <span aria-hidden="true" className="mr-1.5">{MARK[c.state] ?? ''}</span>{checkValue(c, ageDays)}
              </span>
            </li>
          )
        })}
      </ul>
      {footer && <div className="px-5 pb-3 text-12 text-ink-muted">{footer}</div>}
    </section>
  )
}
